import React, { useState, useEffect } from 'react';
import { supabaseAdmin } from '../lib/supabase';

const TABLES = [
  'users', 'user_voca', 'user_settings', 'chat_messages', 'chat_sessions',
  'concept1_quizzes', 'daily_stats', 'login_logs', 'notices',
  'nuance_groups', 'nuance_words', 'quiz_choices', 'scenario_synonyms',
  'scenarios', 'study_records', 'todos', 'word_pairs', 'words',
];

type TableState = {
  preview: any[] | null;   // 1 row
  full: any[] | null;      // all rows
  loading: boolean;
  open: boolean;
  count: number | null;
  expanded: boolean;       // showing full vs preview
};

export default function DBExplorerPage() {
  const [tables, setTables] = useState<Record<string, TableState>>(
    Object.fromEntries(TABLES.map(t => [t, {
      preview: null, full: null,
      loading: true, open: true,
      count: null, expanded: false,
    }]))
  );

  useEffect(() => {
    TABLES.forEach(async (t) => {
      const [{ count }, { data }] = await Promise.all([
        supabaseAdmin.from(t).select('*', { count: 'exact', head: true }),
        supabaseAdmin.from(t).select('*').limit(1),
      ]);
      setTables(prev => ({
        ...prev,
        [t]: { ...prev[t], count: count ?? 0, preview: data ?? [], loading: false },
      }));
    });
  }, []);

  // 단일 펼치기 모드: 하나만 열고 나머지 닫기
  const [singleMode, setSingleMode] = useState(false);

  function toggleOpen(t: string) {
    setTables(prev => ({ ...prev, [t]: { ...prev[t], open: !prev[t].open } }));
  }

  function expandAll() {
    setSingleMode(false);
    setTables(prev => Object.fromEntries(
      Object.entries(prev).map(([k, v]) => [k, { ...v, open: true }])
    ));
  }

  function collapseAll() {
    setSingleMode(false);
    setTables(prev => Object.fromEntries(
      Object.entries(prev).map(([k, v]) => [k, { ...v, open: false }])
    ));
  }

  function openSingle(t: string) {
    setSingleMode(true);
    setTables(prev => Object.fromEntries(
      Object.entries(prev).map(([k, v]) => [k, { ...v, open: k === t }])
    ));
  }

  async function loadAll(t: string) {
    setTables(prev => ({ ...prev, [t]: { ...prev[t], loading: true } }));
    const { data } = await supabaseAdmin.from(t).select('*');
    setTables(prev => ({ ...prev, [t]: { ...prev[t], loading: false, full: data ?? [], expanded: true } }));
  }

  function collapseToPreview(t: string) {
    setTables(prev => ({ ...prev, [t]: { ...prev[t], expanded: false } }));
  }

  return (
    <div style={{ padding: '28px 28px 60px', maxWidth: 1200 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 28 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h1 style={{ fontSize: 24, fontWeight: 900, color: 'white' }}>🗄 DB LIST</h1>
            <span style={{
              fontSize: 13, fontWeight: 800,
              padding: '4px 14px', borderRadius: 999,
              background: 'rgba(167,139,250,0.25)',
              border: '1px solid rgba(167,139,250,0.4)',
              color: '#C4A8FF', letterSpacing: 0.5,
            }}>
              {TABLES.length} tables
            </span>
          </div>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
            개발 참고용 — 기본 최신 1행 미리보기, 전체보기로 모든 데이터 조회
          </p>
        </div>

        {/* 전체 제어 버튼 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, paddingTop: 4 }}>
          <button onClick={expandAll} style={btnStyle('#C4A8FF', 'rgba(167,139,250,0.15)')}>
            전체 펼치기
          </button>
          <button onClick={collapseAll} style={btnStyle('rgba(255,255,255,0.5)', 'rgba(255,255,255,0.08)')}>
            전체 접기
          </button>
          {/* 1개 펼치기 드롭다운 */}
          <select
            onChange={e => { if (e.target.value) openSingle(e.target.value); e.target.value = ''; }}
            defaultValue=""
            style={{
              padding: '5px 10px', borderRadius: 999,
              background: singleMode ? 'rgba(255,184,77,0.15)' : 'rgba(255,255,255,0.06)',
              border: `1px solid ${singleMode ? 'rgba(255,184,77,0.5)' : 'rgba(255,255,255,0.15)'}`,
              color: singleMode ? '#FFB84D' : 'rgba(255,255,255,0.6)',
              fontSize: 12, fontWeight: 700, cursor: 'pointer',
              outline: 'none',
            }}
          >
            <option value="" disabled>1개만 보기 ▾</option>
            {TABLES.map(t => <option key={t} value={t} style={{ background: '#1a1a2e', color: 'white' }}>{t}</option>)}
          </select>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {TABLES.map(t => {
          const st = tables[t];
          if (!st) return null;
          const rows = st.expanded && st.full ? st.full : (st.preview ?? []);
          const cols = rows.length > 0 ? Object.keys(rows[0]) : [];

          return (
            <div key={t} style={{
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 14,
              overflow: 'hidden',
            }}>
              {/* Toggle header */}
              <button
                onClick={() => toggleOpen(t)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                  padding: '13px 18px', background: 'none', border: 'none',
                  cursor: 'pointer', textAlign: 'left',
                }}
              >
                <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', width: 12 }}>
                  {st.open ? '▼' : '▶'}
                </span>
                <span style={{
                  flex: 1, fontSize: 14, fontWeight: 800, fontFamily: 'monospace',
                  color: st.open ? '#C4A8FF' : 'rgba(255,255,255,0.75)',
                }}>
                  {t}
                </span>
                {st.count !== null && (
                  <span style={{
                    fontSize: 11, fontWeight: 700, padding: '2px 10px',
                    borderRadius: 999,
                    background: st.count === 0 ? 'rgba(255,255,255,0.08)' : 'rgba(155,127,212,0.2)',
                    color: st.count === 0 ? 'rgba(255,255,255,0.3)' : '#C4A8FF',
                    flexShrink: 0,
                  }}>
                    {st.count.toLocaleString()} rows
                  </span>
                )}
              </button>

              {/* Content */}
              {st.open && (
                <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)', padding: '0 18px 14px' }}>
                  {st.loading ? (
                    <div style={{ padding: '14px 0', color: 'rgba(255,255,255,0.35)', fontSize: 12 }}>로딩 중...</div>
                  ) : rows.length === 0 ? (
                    <div style={{ padding: '14px 0', color: 'rgba(255,255,255,0.3)', fontSize: 12 }}>데이터 없음</div>
                  ) : (
                    <>
                      <div style={{ overflowX: 'auto', marginTop: 12 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                          <thead>
                            <tr>
                              {cols.map(c => (
                                <th key={c} style={{
                                  padding: '5px 12px', textAlign: 'left',
                                  color: 'rgba(255,255,255,0.45)', fontWeight: 700,
                                  fontSize: 10, letterSpacing: 0.8,
                                  borderBottom: '1px solid rgba(255,255,255,0.07)',
                                  whiteSpace: 'nowrap',
                                }}>{c}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((row, ri) => (
                              <tr key={ri}>
                                {cols.map(c => (
                                  <td key={c} style={{
                                    padding: '8px 12px',
                                    color: 'rgba(255,255,255,0.82)',
                                    borderBottom: ri < rows.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                                    maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                  }}>
                                    {row[c] === null ? (
                                      <span style={{ color: 'rgba(255,255,255,0.18)' }}>null</span>
                                    ) : typeof row[c] === 'boolean' ? (
                                      <span style={{ color: row[c] ? '#34C98A' : '#F07880' }}>{String(row[c])}</span>
                                    ) : Array.isArray(row[c]) ? (
                                      <span style={{ color: '#FFB84D' }}>[{row[c].join(', ')}]</span>
                                    ) : String(row[c])}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Actions */}
                      <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
                        {!st.expanded && (st.count ?? 0) > 1 && (
                          <button onClick={() => loadAll(t)} style={btnStyle('#C4A8FF', 'rgba(167,139,250,0.15)')}>
                            전체보기 ({st.count?.toLocaleString()}개)
                          </button>
                        )}
                        {st.expanded && (
                          <button onClick={() => collapseToPreview(t)} style={btnStyle('rgba(255,255,255,0.5)', 'rgba(255,255,255,0.08)')}>
                            접기
                          </button>
                        )}
                        {!st.expanded && (
                          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)' }}>
                            최신 1행 미리보기
                          </span>
                        )}
                        {st.expanded && (
                          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>
                            전체 {rows.length.toLocaleString()}행
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function btnStyle(color: string, bg: string): React.CSSProperties {
  return {
    padding: '5px 14px', borderRadius: 999,
    background: bg, border: `1px solid ${color}44`,
    color, fontSize: 12, fontWeight: 700, cursor: 'pointer',
  };
}
